import type { Route } from '#/global'
import type { ComputedRef, Ref } from 'vue'
import type { RouteRecordNormalized, RouteRecordRaw, RouterMatcher } from 'vue-router'
import { cloneDeep } from 'es-toolkit'
import { defineStore } from 'pinia' // Removed StoreDefinition import
import { computed, ref } from 'vue'
import { createRouterMatcher } from 'vue-router'
import apiApp from '@/api/modules/app'
import { systemRoutes as systemRoutesRaw } from '@/router/routes'
import useSettingsStore from './settings'
import useTabbarStore from './tabbar'

// This interface describes the exact object returned by the setup function
interface RouteStoreExposed {
  isGenerate: Ref<boolean>
  routesRaw: Ref<Route.recordMainRaw[]>
  currentRemoveRoutes: Ref<(() => void)[]>
  routes: ComputedRef<RouteRecordRaw[]>
  systemRoutes: ComputedRef<RouteRecordRaw[]>
  getRouteMatchedByPath: (path: string) => RouteRecordNormalized[]
  generateRoutesAtFront: (asyncRoutes: Route.recordMainRaw[]) => void
  generateRoutesAtBack: () => Promise<void>
  generateRoutesAtFilesystem: (asyncRoutes: RouteRecordRaw[]) => void
  setCurrentRemoveRoutes: (routes: (() => void)[]) => void
  removeRoutes: () => void
}

// No explicit StoreDefinition type on useRouteStore here.
// Instead, explicitly type the return of the setup function.
const useRouteStore = defineStore(
  // 唯一ID
  'route',
  (): RouteStoreExposed => { // Explicit return type for the setup function
    const settingsStore = useSettingsStore()
    const tabbarStore = useTabbarStore()
    const isGenerate = ref(false)
    const routesRaw = ref<Route.recordMainRaw[]>([])
    const filesystemRoutesRaw = ref<RouteRecordRaw[]>([]) // Internal state, not returned
    const currentRemoveRoutes = ref<(() => void)[]>([])
    const routesMatcher = ref<RouterMatcher | undefined>() // Internal state, not returned

    const routes = computed<RouteRecordRaw[]>(() => {
      const returnRoutes: RouteRecordRaw[] = []
      if (settingsStore.settings.app.routeBaseOn !== 'filesystem') {
        if (routesRaw.value) {
          routesRaw.value.forEach((item) => {
            const tmpRoutes = cloneDeep(item.children) as RouteRecordRaw[]
            tmpRoutes.map((v) => {
              if (!v.meta) {
                v.meta = {}
              }
              v.meta.auth = item.meta?.auth ?? v.meta?.auth
              return v
            })
            returnRoutes.push(...tmpRoutes)
          })
          returnRoutes.forEach((item) => {
            if (item.children) {
              item.children = deleteMiddleRouteComponent(item.children)
            }
            return item
          })
        }
      }
      else {
        returnRoutes.push(...cloneDeep(filesystemRoutesRaw.value) as RouteRecordRaw[])
      }
      return returnRoutes
    })

    const systemRoutes = computed<RouteRecordRaw[]>(() => {
      const routesList = [...systemRoutesRaw] // Use a different variable name
      routesList.forEach((item) => {
        if (item.children) {
          item.children = deleteMiddleRouteComponent(item.children)
        }
      })
      return routesList
    })

    function deleteMiddleRouteComponent(inputRoutes: RouteRecordRaw[]): RouteRecordRaw[] { // Renamed param
      const res: RouteRecordRaw[] = []
      inputRoutes.forEach((route) => {
        const currentRoute = { ...route } // Work with a copy
        if (currentRoute.children?.length) {
          delete currentRoute.component
          currentRoute.children = deleteMiddleRouteComponent(currentRoute.children)
        }
        else {
          delete currentRoute.children
        }
        res.push(currentRoute)
      })
      return res
    }

    function getRouteMatchedByPath(path: string): RouteRecordNormalized[] {
      return routesMatcher.value?.resolve({ path }, undefined!)?.matched ?? []
    }

    function sortAsyncRoutes<T extends Route.recordMainRaw[] | RouteRecordRaw[]>(inputRoutes: T): T { // Renamed param
      inputRoutes.sort((a, b) => (b.meta?.sort ?? 0) - (a.meta?.sort ?? 0))
      inputRoutes.forEach((route) => {
        if (route.children) {
          route.children = sortAsyncRoutes(route.children as T extends (infer R)[] ? R[] extends Route.recordMainRaw[] | RouteRecordRaw[] ? R[] : never : never)
        }
      })
      return inputRoutes
    }

    function convertSingleRoutes<T extends Route.recordMainRaw[]>(inputRoutes: T): T { // Renamed param
      inputRoutes.map((route) => {
        if (route.children) {
          route.children.forEach((item, index, arr) => {
            if (item.meta?.singleMenu) {
              const originalItem = { ...item } // Work with a copy
              arr[index] = {
                ...originalItem,
                component: () => import('@/layouts/index.vue'),
                children: [
                  {
                    path: '',
                    name: originalItem.name,
                    component: originalItem.component,
                    meta: {
                      ...(originalItem.meta || {}), // Ensure meta exists
                      title: originalItem.meta!.title, // originalItem.meta is checked by singleMenu
                      menu: false,
                      breadcrumb: false,
                    },
                  },
                ],
              } as RouteRecordRaw
              delete arr[index].name
              if (arr[index].meta) { // Check meta exists before deleting property
                delete (arr[index].meta as { singleMenu?: boolean })!.singleMenu
              }
            }
          })
        }
        return route
      })
      return inputRoutes
    }

    function generateRoutesAtFront(asyncRoutes: Route.recordMainRaw[]): void {
      routesRaw.value = convertSingleRoutes(sortAsyncRoutes(cloneDeep(asyncRoutes) as any))
      const tempRoutes: RouteRecordRaw[] = []
      routesRaw.value.forEach((route) => {
        if (route.children) {
          tempRoutes.push(...route.children)
        }
      })
      routesMatcher.value = createRouterMatcher(tempRoutes, {})
      isGenerate.value = true
      if (settingsStore.settings.tabbar.enable) {
        tabbarStore.initPermanentTab()
      }
    }

    function formatBackRoutes(inputRoutes: any, views = import.meta.glob('../../views/**/*.vue')): Route.recordMainRaw[] { // Renamed param
      return inputRoutes.map((route: any) => {
        const currentRoute = { ...route } // Work with a copy
        switch (currentRoute.component) {
          case 'Layout':
            currentRoute.component = () => import('@/layouts/index.vue')
            break
          default:
            if (currentRoute.component) {
              currentRoute.component = views[`../../views/${currentRoute.component}`]
            }
            else {
              delete currentRoute.component
            }
        }
        if (currentRoute.children) {
          currentRoute.children = formatBackRoutes(currentRoute.children, views)
        }
        return currentRoute
      })
    }

    async function generateRoutesAtBack(): Promise<void> {
      try {
        const res = await apiApp.routeList()
        routesRaw.value = convertSingleRoutes(sortAsyncRoutes(formatBackRoutes(res.data) as any))
        const tempRoutes: RouteRecordRaw[] = []
        routesRaw.value.forEach((route) => {
          if (route.children) {
            tempRoutes.push(...route.children)
          }
        })
        routesMatcher.value = createRouterMatcher(tempRoutes, {})
        isGenerate.value = true
        if (settingsStore.settings.tabbar.enable) {
          tabbarStore.initPermanentTab()
        }
      }
      catch (error) {
        console.error(error)
      }
    }

    function generateRoutesAtFilesystem(asyncRoutes: RouteRecordRaw[]): void {
      filesystemRoutesRaw.value = convertSingleRoutes(sortAsyncRoutes(cloneDeep(asyncRoutes) as any) as any)
      isGenerate.value = true
      if (settingsStore.settings.tabbar.enable) {
        tabbarStore.initPermanentTab()
      }
    }

    function setCurrentRemoveRoutes(inputRoutes: (() => void)[]): void { // Renamed param
      currentRemoveRoutes.value = inputRoutes
    }

    function removeRoutes(): void {
      isGenerate.value = false
      routesRaw.value = []
      filesystemRoutesRaw.value = []
      currentRemoveRoutes.value.forEach((removeRoute) => {
        removeRoute()
      })
      currentRemoveRoutes.value = []
    }

    // This returned object must exactly match the RouteStoreExposed interface
    return {
      isGenerate,
      routesRaw,
      currentRemoveRoutes,
      routes,
      systemRoutes,
      getRouteMatchedByPath,
      generateRoutesAtFront,
      generateRoutesAtBack,
      generateRoutesAtFilesystem,
      setCurrentRemoveRoutes,
      removeRoutes,
    }
  },
)

export default useRouteStore
